import { LogisticsProjectLookupComponent } from '../../../../logistics-projects/logistics-project-lookup.component';
import { isEditableFinancialDraft } from '../../../shared/accounting-draft-eligibility';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { MenuItem, MessageService } from 'primeng/api';
import { SessionStorageService } from 'src/app/services/session-storage.service';
import { forkJoin, of } from 'rxjs';
import { catchError, finalize, map } from 'rxjs/operators';
import { ActivatedRoute, Router } from '@angular/router';
import { AccountsService } from '../../../accounts.service';
import { APIListWrapper } from '../../../../models/common/api-list-wrapper.model';
import { Invoice } from '../../../invoice/invoice.model';
import { BankAccountService } from "../../../bank-account/bank-account.service";
import { BankAccount } from "../../../bank-account/bank-account.model";
import { Party } from "../../../../party/party.model";
import { PartyService } from '../../../../party/party.service';
import { DialogService, DynamicDialogModule, DynamicDialogRef } from 'primeng/dynamicdialog';
import { BankAccountEntryComponent } from '../../../bank-account/bank-account-entry/bank-account-entry.component';
import { Receipt } from 'src/app/accounts/receipt/receipt.model';
import { SelectModule } from 'primeng/select';
import { DatePickerModule } from 'primeng/datepicker';
import { CommonModule } from '@angular/common';
import { BreadcrumbModule } from 'primeng/breadcrumb';
import { ButtonModule } from 'primeng/button';
import { CheckboxModule } from 'primeng/checkbox';
import { DialogModule } from 'primeng/dialog';
import { InputNumberModule } from 'primeng/inputnumber';
import { InputTextModule } from 'primeng/inputtext';
import { PanelModule } from 'primeng/panel';
import { RippleModule } from 'primeng/ripple';
import { TableModule } from 'primeng/table';
import { ToastModule } from 'primeng/toast';
import { ProgressBarModule } from "primeng/progressbar";
import { InputGroupModule } from "primeng/inputgroup";
import { InputGroupAddonModule } from "primeng/inputgroupaddon";
import { IftaLabelModule } from "primeng/iftalabel";
import { PermissionService } from '../../../../security/permission.service';
import { app_permissions } from '../../../../security/app-permissions';
import { TextareaModule } from 'primeng/textarea';
import { LogisticsProjectsService } from '../../../../logistics-projects/logistics-projects.service';
import { TooltipModule } from 'primeng/tooltip';
import { PageShellComponent } from '../../../../shared/page-layout/page-shell.component';
import { PageHeroComponent } from '../../../../shared/page-layout/page-hero.component';
import { PageMetricStripComponent } from '../../../../shared/page-layout/page-metric-strip.component';
import { PageMetric } from '../../../../shared/page-layout/page-layout.models';
import { PageFormSectionComponent } from '../../../../shared/page-layout/page-form-section.component';
import { PageDataPanelComponent } from '../../../../shared/page-layout/page-data-panel.component';
import { CrmClient, CrmClientCommunication } from '../../../../crm/crm.model';
import { CrmService } from '../../../../crm/crm.service';
import { PartyType } from '../../../../party/party-type.enum';
import { CommercialPartyPickerComponent } from '../../../../shared/commercial-party-picker/commercial-party-picker.component';
import { CommercialPartyReference } from '../../../../shared/commercial-party-picker/commercial-party.model';
import { CrmQuickCreateResult } from '../../../../shared/crm-quick-create/crm-quick-create-dialog.component';
import { ChartOfAccounts } from '../../../../chart-of-accounts/chart-of-accounts.model';
import { ChartOfAccountsService } from '../../../../chart-of-accounts/chart-of-accounts.service';

// Interface for outstanding references (Invoices/Orders)
interface OutstandingReference {
    id: number;
    type: string;
    name: string;
    date: Date;
    grandTotal: number;
    outstandingAmount: number;
    allocatedAmount: number;
    selected: boolean;
    logisticsProjectId?: number;
    currencyCode?: string;
}

@Component({
    selector: 'app-receipt-entry',
    templateUrl: './receipt-entry.component.html',
    styleUrls: ['./receipt-entry.component.css'],
    standalone: true,
    imports: [
        LogisticsProjectLookupComponent,
        ReactiveFormsModule,
        FormsModule,
        CommonModule,
        InputTextModule,
        InputNumberModule,
        ButtonModule,
        RippleModule,
        SelectModule,
        DatePickerModule,
        ToastModule,
        PanelModule,
        BreadcrumbModule,
        TableModule,
        CheckboxModule,
        DialogModule,
        ProgressBarModule,
        DynamicDialogModule,
        InputGroupModule,
        InputGroupAddonModule,
        TextareaModule,
        IftaLabelModule,
        TooltipModule,
        PageShellComponent,
        PageHeroComponent,
        PageMetricStripComponent,
        PageFormSectionComponent,
        PageDataPanelComponent,
        CommercialPartyPickerComponent
    ],
    providers: [
        MessageService,
        AccountsService,
        SessionStorageService,
        BankAccountService,
        CrmService,
        DialogService
    ]
})
export class ReceiptEntryComponent implements OnInit, OnDestroy {

    commercialPartyReference: CommercialPartyReference = { source: 'CRM' };

    // --- Class Properties ---
    isLoading: boolean = false;
    breadcrumbItems: MenuItem[];
    homeBreadcrumb: MenuItem;

    receiptEntryForm: FormGroup;
    editMode: boolean = false;
    receiptId: number | null = null;
    loadedReceipt: Receipt | null = null;

    // Dropdown options
    modeOfPaymentOptions: string[] = ['Cash', 'Bank Transfer', 'Cheque'];
    companyBankAccounts: BankAccount[] = [];
    chartAccounts: ChartOfAccounts[] = [];
    ledgerDefaults: {cashAccountId: number | null; receivableAccountId: number | null; payableAccountId: number | null} | null = null;
    crmClients: CrmClient[] = [];
    parties: Party[] = [];
    crmContacts: CrmClientCommunication[] = [];
    partyBankAccounts: BankAccount[] = [];

    // Allocation properties
    outstandingReferences: OutstandingReference[] = []; // Corrected type
    totalAllocatedAmount: number = 0;
    unallocatedAmount: number = 0;
    differenceAmount: number = 0;

    canSave = false;

    private dialogRef: DynamicDialogRef | undefined;

    get pageTitle(): string {
        return this.editMode ? 'Edit Receipt Entry' : 'New Receipt Entry';
    }

    get requiresBankAccount(): boolean {
        return this.receiptEntryForm.get('modeOfPayment')?.value !== 'Cash';
    }

    get selectedPartyName(): string {
        return this.receiptEntryForm.get('crmClient')?.value?.companyName || 'No client selected';
    }

    get selectedCurrencyCode(): string {
        return this.receiptEntryForm.get('currencyCode')?.value || 'SAR';
    }

    get metrics(): PageMetric[] {
        return [
            {
                label: 'Receipt amount',
                value: this.formatMoney(this.receiptEntryForm.get('paidAmount')?.value),
                hint: this.receiptEntryForm.get('modeOfPayment')?.value || 'Payment mode pending',
                tone: 'info'
            },
            {
                label: 'Allocated',
                value: this.formatMoney(this.totalAllocatedAmount),
                hint: `${this.selectedReferenceCount} references selected`,
                tone: this.totalAllocatedAmount > 0 ? 'success' : 'default'
            },
            {
                label: 'Unallocated',
                value: this.formatMoney(this.unallocatedAmount),
                hint: 'Remaining receipt balance',
                tone: this.unallocatedAmount > 0 ? 'warn' : 'success'
            },
            {
                label: 'Outstanding refs',
                value: this.outstandingReferences.length,
                hint: this.selectedPartyName
            }
        ];
    }

    get selectedReferenceCount(): number {
        return this.outstandingReferences.filter(ref => ref.selected).length;
    }

    constructor(
        private fb: FormBuilder,
        private messageService: MessageService,
        private sessionStorageService: SessionStorageService,
        private accountingService: AccountsService,
        private chartOfAccountsService: ChartOfAccountsService,
        private bankAccountService: BankAccountService,
        private route: ActivatedRoute,
        private router: Router,
        private dialogService: DialogService,
        private permissionService: PermissionService,
        private logisticsProjectsService: LogisticsProjectsService,
        private crmService: CrmService,
        private partyService: PartyService
    ) {
        // Form initialization remains the same
        this.receiptEntryForm = this.fb.group({
            postingDate: [new Date(), Validators.required],
            modeOfPayment: ['Cash', Validators.required],
            cashAccountId: [null],
            counterpartAccountId: [null],
            companyBankAccount: [null],
            paymentTransactionNumber: [''],
            party: [null, Validators.required],
            crmClient: [null],
            crmClientContact: [null],
            partyBankAccount: [null],
            paidAmount: [null, [Validators.required, Validators.min(0.01)]],
            logisticsProjectId: [Number(this.route.snapshot.queryParamMap.get('projectId') || '') || null],
            currencyCode: [{ value: 'SAR', disabled: true }],
            remarks: [''],
        });
    }

    ngOnInit(): void {
        this.breadcrumbItems = [{ label: 'Accounts' }, { label: 'Receipts' }, { label: 'New Receipt' }];
        this.homeBreadcrumb = { icon: 'pi pi-home', routerLink: '/' };

        this.route.params.subscribe(params => {
            if (params['id']) {
                this.receiptId = +params['id'];
                this.editMode = true;
                this.breadcrumbItems[2] = { label: `Edit Receipt` };
            }
        });

        if (this.editMode) {
            this.canSave = this.permissionService.hasPermission(app_permissions.accounts.receipt.edit);
        } else {
            this.canSave = this.permissionService.hasPermission(app_permissions.accounts.receipt.create);
        }

        this.loadInitialData();
    }

    ngOnDestroy(): void {
        if (this.dialogRef) {
            this.dialogRef.close();
        }
    }

    loadInitialData(): void {
        this.isLoading = true;
        const companyId = this.sessionStorageService.getCompany()?.id;
        if (!companyId) {
            this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Company ID not found.' });
            this.isLoading = false;
            return;
        }

        forkJoin({
            companyBankAccounts: this.bankAccountService.getBankAccounts('Company', companyId, '', 0, 1000),
            crmClients: this.crmService.getClients(companyId, ['CUSTOMER']),
            parties: this.partyService.getParties(companyId, Object.values(PartyType), '', 0, 1000),
            chartAccounts: this.chartOfAccountsService.getAllChartOfAccountsByCompany(companyId, false).pipe(catchError(() => of([] as ChartOfAccounts[]))),
            ledgerDefaults: this.accountingService.getPaymentLedgerDefaults(companyId).pipe(catchError(() => of(null)))
        }).pipe(
            finalize(() => this.isLoading = false),
            catchError(error => {
                this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Failed to load initial data.' });
                console.error('Initial data load error:', error);
                return of({ companyBankAccounts: null, crmClients: [], parties: null, chartAccounts: [], ledgerDefaults: null });
            })
        ).subscribe(data => {
            this.companyBankAccounts = data.companyBankAccounts?.items ?? [];
            this.crmClients = data.crmClients ?? [];
            this.parties = data.parties?.content ?? [];
            this.chartAccounts = data.chartAccounts ?? [];
            this.ledgerDefaults = data.ledgerDefaults;
            if (this.receiptId) {
                this.loadReceiptData(this.receiptId);
            }
        });
    }

    loadReceiptData(id: number): void {
        this.isLoading = true;
        this.accountingService.getReceiptById(id).pipe(
            finalize(() => this.isLoading = false)
        ).subscribe({
            next: (receipt) => {
                this.loadedReceipt = receipt;
                if (!isEditableFinancialDraft(receipt, this.sessionStorageService.getCompany()?.id)) {
                    this.canSave = false;
                    this.receiptEntryForm.disable();
                    this.messageService.add({ severity: 'error', summary: 'Draft unavailable', detail: 'Only unposted Draft documents for the selected company can be edited.' });
                    return;
                }
                const companyBankAccount = receipt.toBankAccount || null;
                const partyBankAccount = receipt.fromBankAccount || null;
                const crmClient = this.crmClients.find(client => client.id === receipt.crmClientId)
                    || this.crmClients.find(client => this.partyMatchesClient(receipt.mainParty, client))
                    || null;

                this.receiptEntryForm.patchValue({
                    remarks: receipt.manualReasonText || '',
                    postingDate: receipt.receiptDate ? new Date(receipt.receiptDate) : new Date(),
                    modeOfPayment: this.displayPaymentMode(receipt.paymentMode)
                        || receipt.personalPaymentMethod?.paymentMethod?.paymentMethodName
                        || (receipt.toBankAccount ? 'Bank Transfer' : 'Cash'),
                    cashAccountId: receipt.cashAccountId ?? null,
                    counterpartAccountId: receipt.counterpartAccountId ?? null,
                    paidAmount: receipt.amount,
                    logisticsProjectId: receipt.logisticsProjectId || this.receiptEntryForm.get('logisticsProjectId')?.value,
                    party: receipt.mainParty || null,
                    crmClient,
                    companyBankAccount,
                    partyBankAccount,
                    paymentTransactionNumber: receipt.paymentTransactionNumber || '',
                    currencyCode: receipt.country?.currencyCode || this.selectedCurrencyCode
                });
                this.commercialPartyReference = {
                    source: receipt.crmClientId ? 'CRM' : 'LEGACY', crmClientId: receipt.crmClientId,
                    crmAccountId: receipt.crmAccountId, crmContactId: receipt.crmClientContactId,
                    legacyCompanyId: receipt.mainParty?.id?.entityType === PartyType.Company ? receipt.mainParty.id.entityId : undefined,
                    legacyCustomerId: receipt.subParty?.id?.entityId
                        ?? (receipt.mainParty?.id?.entityType === PartyType.Customer ? receipt.mainParty.id.entityId : undefined)
                };
                if (crmClient) {
                    this.loadCrmContacts(crmClient, receipt.crmClientContactId);
                }

                // --- Populate Allocations (UPDATED LOGIC) ---
                if (receipt.invoices && receipt.invoices.length > 0) {
                    this.outstandingReferences = receipt.invoices.map((inv: Invoice) => {
                        // The amount of *this specific receipt* that was allocated to the invoice.
                        const allocatedForThisReceipt = inv.receipts?.find(r => r.receiptId === receipt.receiptId)?.amount ?? 0;

                        // The currently outstanding amount on the invoice.
                        const currentOutstanding = this.invoiceGrossAmount(inv) -
                            (inv.receipts?.reduce((sum, r) => sum + (r.amount || 0), 0) || 0);

                        // For display, show what was outstanding *before* this receipt was applied.
                        const outstandingBeforeThisReceipt = currentOutstanding + allocatedForThisReceipt;

                        return {
                            id: inv.invoiceId!,
                            type: 'Sales Invoice',
                            name: inv.invoiceNumber!,
                            date: new Date(inv.invoiceCreationDate!),
                            grandTotal: this.invoiceGrossAmount(inv),
                            outstandingAmount: outstandingBeforeThisReceipt,
                            allocatedAmount: allocatedForThisReceipt,
                            selected: true,
                            logisticsProjectId: inv.logisticsProjectId,
                            currencyCode: inv.country?.currencyCode || receipt.country?.currencyCode || this.selectedCurrencyCode
                        };
                    });
                    this.calculateAllocationTotals();
                }
            },
            error: (err) => {
                this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Failed to load receipt data.' });
                console.error('Load receipt data error:', err);
            }
        });
    }

    onCrmClientSelect(): void {
        const selectedClient = this.receiptEntryForm.get('crmClient')?.value as CrmClient | null;
        this.partyBankAccounts = [];
        this.crmContacts = [];
        this.receiptEntryForm.get('crmClientContact')?.setValue(null);
        this.receiptEntryForm.get('partyBankAccount')?.setValue(null);
        this.outstandingReferences = [];
        this.calculateAllocationTotals();

        if (!selectedClient) {
            return;
        }

        this.receiptEntryForm.get('currencyCode')?.setValue(selectedClient.currencyCode || this.defaultCurrencyCode());
        this.loadCrmContacts(selectedClient, this.commercialPartyReference.crmContactId ?? undefined);
        const party = this.partyFromCrmClient(selectedClient);
        if (!party) {
            this.messageService.add({ severity: 'warn', summary: 'CRM link required', detail: 'Selected CRM client is not linked to a company or customer party.' });
            return;
        }
        this.receiptEntryForm.get('party')?.setValue(party, { emitEvent: false });
        this.loadPartyBankAccounts(party);
        this.getOutstandingInvoices();
    }

    onCrmClientPicked(client: CrmClient | null): void {
        this.receiptEntryForm.get('crmClient')?.setValue(client);
        this.onCrmClientSelect();
    }

    get commercialPartyCompanyId(): number | undefined { return this.sessionStorageService.getCompany()?.id; }

    onCrmQuickCreated(result: CrmQuickCreateResult): void {
        if (result.mode === 'client') this.crmClients = [...this.crmClients, result.record];
    }

    onCommercialPartyChange(reference: CommercialPartyReference): void {
        this.commercialPartyReference = reference;
        if (reference.source === 'CRM') {
            const client = this.crmClients.find(item => item.id === reference.crmClientId) || null;
            this.onCrmClientPicked(client);
            this.receiptEntryForm.patchValue({
                crmClient: client,
                crmClientContact: this.crmContacts.find(item => item.id === reference.crmContactId) || null
            });
            return;
        }
        const partyId = reference.legacyCustomerId ?? reference.legacyCompanyId;
        const party = this.parties.find(item => item.id.entityId === partyId) || null;
        this.receiptEntryForm.patchValue({ party, crmClient: null, crmClientContact: null });
        this.onPartyPicked(party);
    }

    onCrmContactPicked(contact: CrmClientCommunication | null): void {
        this.receiptEntryForm.get('crmClientContact')?.setValue(contact);
    }

    onPartyPicked(party: Party | null): void {
        this.partyBankAccounts = [];
        this.outstandingReferences = [];
        this.calculateAllocationTotals();
        this.receiptEntryForm.get('partyBankAccount')?.setValue(null);
        if (this.receiptEntryForm.get('crmClient')?.value) {
            this.receiptEntryForm.patchValue({ crmClient: null, crmClientContact: null }, { emitEvent: false });
            this.crmContacts = [];
        }
        if (party) {
            this.loadPartyBankAccounts(party);
            this.getOutstandingInvoices();
        }
    }

    private loadCrmContacts(client: CrmClient, selectedContactId?: number): void {
        if (!client.id) {
            this.crmContacts = [];
            return;
        }
        const companyId = this.sessionStorageService.getCompany()?.id;
        this.crmService.getClientMasterCommunications(client.id, companyId).subscribe({
            next: contacts => {
                this.crmContacts = contacts || [];
                const selected = selectedContactId
                    ? this.crmContacts.find(contact => contact.id === selectedContactId)
                    : this.crmContacts.find(contact => contact.primaryRecord) || this.crmContacts[0];
                this.receiptEntryForm.get('crmClientContact')?.setValue(selected || null);
            },
            error: error => {
                this.crmContacts = [];
                this.messageService.add({ severity: 'warn', summary: 'Contacts', detail: error?.error?.message || 'Could not load CRM contacts.' });
            }
        });
    }

    private loadPartyBankAccounts(selectedParty: Party): void {
        this.bankAccountService.getBankAccounts(selectedParty.id.entityType, selectedParty.id.entityId, '', 0, 1000).subscribe({
            next: (bankAccounts) => {
                this.partyBankAccounts = bankAccounts.items;
                if (!this.receiptEntryForm.get('partyBankAccount')?.value) {
                    this.receiptEntryForm.get('partyBankAccount')?.setValue(null);
                }
            },
            error: (err) => {
                this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Failed to load party bank accounts.' });
                console.error('Party bank accounts load error:', err);
            }
        });
    }

    getOutstandingInvoices(): void {
        const party = this.receiptEntryForm.get('party')?.value as Party | null;
        const companyId = this.sessionStorageService.getCompany()?.id;

        if (!party?.id || !companyId) {
            this.messageService.add({ severity: 'warn', summary: 'Selection Required', detail: 'Please select a party first.' });
            return;
        }

        this.isLoading = true;
        this.accountingService.getGeneratedInvoices(0, 500, companyId, undefined, undefined, undefined, undefined, undefined, {
            postingStatuses: ['POSTED'],
            paymentStatuses: ['UNPAID', 'PARTIALLY_PAID'],
            currencyCode: this.selectedCurrencyCode,
            partyId: party.id.entityId,
            partyType: party.id.entityType
        }).pipe(
            finalize(() => this.isLoading = false)
        ).subscribe({
            next: (invoices: APIListWrapper<Invoice>) => {
                this.outstandingReferences = (invoices.items ?? []).map(inv => {
                    const outstanding = inv.openAmount ?? (this.invoiceGrossAmount(inv) -
                        (inv.receipts?.reduce((sum, r) => sum + (r.amount || 0), 0) || 0));
                    return {
                        id: inv.invoiceId!,
                        type: 'Sales Invoice',
                        name: inv.invoiceNumber!,
                        date: new Date(inv.invoiceCreationDate!),
                        grandTotal: this.invoiceGrossAmount(inv),
                        outstandingAmount: outstanding,
                        allocatedAmount: 0,
                        selected: false,
                        logisticsProjectId: inv.logisticsProjectId,
                        currencyCode: inv.country?.currencyCode || this.selectedCurrencyCode
                    };
                }).filter(reference => reference.outstandingAmount > 0);
                this.calculateAllocationTotals();
            },
            error: (err) => {
                this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Failed to load outstanding invoices.' });
            }
        });
    }

    onReferenceSelect(ref: OutstandingReference): void {
        if (ref.selected) {
            const paidAmount = this.receiptEntryForm.get('paidAmount')?.value || 0;
            const remainingUnallocated = paidAmount - this.totalAllocatedAmount;
            ref.allocatedAmount = Math.min(ref.outstandingAmount, remainingUnallocated > 0 ? remainingUnallocated : 0);
            if (!this.receiptEntryForm.get('logisticsProjectId')?.value && ref.logisticsProjectId) {
                this.receiptEntryForm.get('logisticsProjectId')?.setValue(ref.logisticsProjectId);
            }
        } else {
            ref.allocatedAmount = 0;
        }
        this.calculateAllocationTotals();
    }

    onAllocationChange(ref: OutstandingReference): void {
        if (ref.allocatedAmount > ref.outstandingAmount) {
            ref.allocatedAmount = ref.outstandingAmount;
        }
        this.calculateAllocationTotals();
    }

    calculateAllocationTotals(): void {
        this.totalAllocatedAmount = this.outstandingReferences
            .filter(ref => ref.selected)
            .reduce((sum, ref) => sum + (ref.allocatedAmount || 0), 0);

        const paidAmount = this.receiptEntryForm.get('paidAmount')?.value || 0;
        this.unallocatedAmount = paidAmount - this.totalAllocatedAmount;
        this.differenceAmount = this.unallocatedAmount;
    }

    private invoiceGrossAmount(invoice: Invoice): number {
        if (invoice.totalAmountExclTax != null && invoice.totalTax != null) {
            return Math.max(0, invoice.totalAmountExclTax + invoice.totalTax);
        }
        return invoice.totalAmount ?? 0;
    }

    formatMoney(amount: number | null | undefined, currencyCode: string = this.selectedCurrencyCode): string {
        return `${currencyCode} ${Number(amount || 0).toLocaleString(undefined, { minimumFractionDigits: 4, maximumFractionDigits: 4 })}`;
    }

    /**
     * Handles changes to the Mode of Payment dropdown.
     * You can add logic here, e.g., to make the bank account field
     * required only for 'Bank Transfer' or 'Cheque'.
     */
    onModeOfPaymentChange(): void {
        const mode = this.receiptEntryForm.get('modeOfPayment')?.value;
        const bankAccountControl = this.receiptEntryForm.get('companyBankAccount');
        const paymentReferenceControl = this.receiptEntryForm.get('paymentTransactionNumber');

        if (mode === 'Cash') {
            bankAccountControl?.setValue(null);
            bankAccountControl?.clearValidators();
            paymentReferenceControl?.clearValidators();
        } else {
            this.receiptEntryForm.get('cashAccountId')?.setValue(null);
            bankAccountControl?.setValidators([Validators.required]);
            paymentReferenceControl?.setValidators([Validators.required]);
        }
        bankAccountControl?.updateValueAndValidity();
        paymentReferenceControl?.updateValueAndValidity();
    }

    get cashAccounts(): ChartOfAccounts[] {
        return this.eligibleAccounts('CASH');
    }

    get eligibleBankAccounts(): BankAccount[] {
        return this.companyBankAccounts.filter(bank => bank.account?.accountId
            && bank.owningCompanyId === this.sessionStorageService.getCompany()?.id
            && bank.account.owningCompanyId === bank.owningCompanyId
            && bank.account.isActive && !bank.account.deprecated
            && (!bank.account.currencyCode || bank.account.currencyCode.toUpperCase() === this.selectedCurrencyCode.toUpperCase())
            && bank.currencyCode?.toUpperCase() === this.selectedCurrencyCode.toUpperCase());
    }

    get counterpartAccounts(): ChartOfAccounts[] {
        return this.eligibleAccounts('AR');
    }

    private eligibleAccounts(type: string): ChartOfAccounts[] {
        return this.chartAccounts.filter(account => account.isActive && !account.deprecated
            && account.owningCompanyId === this.sessionStorageService.getCompany()?.id
            && (!account.currencyCode || account.currencyCode.toUpperCase() === this.selectedCurrencyCode.toUpperCase())
            && account.accountType?.code?.toUpperCase() === type)
            .map(account => ({ ...account, accountName: `${account.accountCode} — ${account.accountName}` }));
    }

    accountLabel(accountId: number | null | undefined): string {
        const account = this.chartAccounts.find(item => item.accountId === accountId);
        return account ? `${account.accountCode} — ${account.accountName}` : 'Unavailable';
    }

    private displayPaymentMode(mode?: string): string | null {
        return mode === 'CASH' ? 'Cash' : mode === 'BANK_TRANSFER' ? 'Bank Transfer'
            : mode === 'CHEQUE' ? 'Cheque' : null;
    }

    /**
     * Handles changes to the Paid Amount input.
     * This is a good place to recalculate allocations if needed.
     */
    onPaidAmountChange(): void {
        this.calculateAllocationTotals();
    }

    saveReceiptEntry(): void {
        if (!this.canSave || (this.editMode && !isEditableFinancialDraft(this.loadedReceipt, this.sessionStorageService.getCompany()?.id))) return;
        this.isLoading = true;
        if (this.receiptEntryForm.invalid) {
            this.messageService.add({ severity: 'error', summary: 'Validation Error', detail: 'Please fill all required fields.' });
            this.receiptEntryForm.markAllAsTouched();
            this.isLoading = false;
            return;
        }
        this.calculateAllocationTotals();
        if (this.totalAllocatedAmount > (this.receiptEntryForm.get('paidAmount')?.value || 0)) {
            this.messageService.add({ severity: 'error', summary: 'Validation Error', detail: 'Allocated amount cannot exceed receipt amount.' });
            this.isLoading = false;
            return;
        }

        const party = this.receiptEntryForm.get('party')?.value as Party | null;
        const crmClient = this.receiptEntryForm.get('crmClient')?.value as CrmClient | null;
        const crmContact = this.receiptEntryForm.get('crmClientContact')?.value as CrmClientCommunication | null;
        if (!party?.id) {
            this.messageService.add({ severity: 'error', summary: 'Party required', detail: 'Select the customer or other receiving party before saving.' });
            this.isLoading = false;
            return;
        }
        if (this.hasCurrencyMismatch()) {
            this.messageService.add({ severity: 'error', summary: 'Currency mismatch', detail: 'Selected allocations must match the receipt currency.' });
            this.isLoading = false;
            return;
        }

        let receipt = {
            receiptId: this.receiptId || undefined,
            receiptNumber: this.loadedReceipt?.receiptNumber,
            documentStatus: this.loadedReceipt?.documentStatus,
            workflowStatus: this.loadedReceipt?.workflowStatus,
            postingStatus: this.loadedReceipt?.postingStatus,
            journalEntryId: this.loadedReceipt?.journalEntryId,
            paymentMode: this.receiptEntryForm.get('modeOfPayment')?.value === 'Cash' ? 'CASH'
                : this.receiptEntryForm.get('modeOfPayment')?.value === 'Cheque' ? 'CHEQUE' : 'BANK_TRANSFER',
            cashAccountId: this.receiptEntryForm.get('modeOfPayment')?.value === 'Cash'
                ? this.receiptEntryForm.get('cashAccountId')?.value || undefined : undefined,
            counterpartAccountId: this.receiptEntryForm.get('counterpartAccountId')?.value || undefined,
            receiptDate: this.receiptEntryForm.get('postingDate')?.value || new Date(),
            isDigitalPayment: this.receiptEntryForm.get('modeOfPayment')?.value === 'Bank Transfer',
            paymentTransactionNumber: this.receiptEntryForm.get('paymentTransactionNumber')?.value || undefined,
            amount: this.receiptEntryForm.get('paidAmount')?.value || 0,
            logisticsProjectId: this.receiptEntryForm.get('logisticsProjectId')?.value || undefined,
            country: this.sessionStorageService.getCountryList()!
                .find(value => value.id == this.sessionStorageService.getCompany()?.countryId),
            mainParty: party,
            subParty: party,
            crmClientId: this.commercialPartyReference.crmClientId ?? crmClient?.id,
            crmAccountId: this.commercialPartyReference.crmAccountId,
            crmClientContactId: this.commercialPartyReference.crmContactId ?? crmContact?.id,
            owningCompany: this.sessionStorageService.getCompany(),
            fromBankAccount: this.receiptEntryForm.get('modeOfPayment')?.value === 'Cash'
                ? undefined : this.receiptEntryForm.get('partyBankAccount')?.value,
            toBankAccount: this.receiptEntryForm.get('modeOfPayment')?.value === 'Cash'
                ? undefined : this.receiptEntryForm.get('companyBankAccount')?.value,
            sourceType: this.loadedReceipt?.sourceType || 'MANUAL',
            sourceReference: this.loadedReceipt?.sourceType && this.loadedReceipt.sourceType !== 'MANUAL' ? this.loadedReceipt.sourceReference : this.paymentSourceReference(),
            manualReasonCode: 'CUSTOMER_PAYMENT',
            manualReasonText: this.receiptEntryForm.get('remarks')?.value || 'Manual customer receipt entered from portal.',
            manualAttachmentRequired: false,
            createdFromModule: 'ACCOUNTS_RECEIPT_ENTRY',
            requestedBy: this.sessionStorageService.getCustomer()?.id,
            createdAt: new Date(),
            updatedAt: new Date(),
        } as Receipt;
        const allocations = this.outstandingReferences
            .filter(ref => ref.selected && ref.allocatedAmount > 0)
            .map(ref => ({
                invoiceId: ref.id,
                allocatedAmount: ref.allocatedAmount
            }));
        const saveRequest = this.receiptId
            ? this.accountingService.createReceipt(receipt)
            : allocations.length > 0
            ? this.accountingService.createReceiptWithAllocations(receipt, { allocations })
            : this.accountingService.createReceipt(receipt);
        saveRequest.subscribe({
            next: created => {
                this.messageService.add({ severity: 'success', summary: 'Success', detail: 'Receipt saved successfully.' });
                this.linkProjectRequirementFulfillment(created).add(() => {
                    this.isLoading = false;
                    this.router.navigate(['/accounts/enterprise/receivables/generated-receipts']);
                });
            },
            error: (err) => {
                this.messageService.add({ severity: 'error', summary: 'Save Failed', detail: 'Failed to save receipt.' + err.error.message });
                console.error('Save receipt error:', err);
                this.isLoading = false;
            }
        });
    }

    private linkProjectRequirementFulfillment(created: Receipt) {
        const projectId = Number(this.route.snapshot.queryParamMap.get('projectId') || 0);
        const requirementId = Number(this.route.snapshot.queryParamMap.get('requirementId') || 0);
        const owningCompanyId = Number(this.sessionStorageService.getCompany()?.id || 0);
        const targetResourceId = Number(created.receiptId || this.receiptId || 0);
        if (!projectId || !requirementId || !owningCompanyId || !targetResourceId) {
            return { add: (callback: () => void) => callback() };
        }
        return this.logisticsProjectsService.linkRequirementFulfillment(projectId, owningCompanyId, requirementId, {
            targetResourceType: this.route.snapshot.queryParamMap.get('targetResourceType') || 'PAYMENT_RECEIPT',
            targetResourceId,
            sourceModule: 'ACCOUNTS_RECEIVABLE',
            sourceReference: created.receiptNumber || `Receipt #${targetResourceId}`,
            fulfilledQuantity: Number(this.route.snapshot.queryParamMap.get('quantity') || 1),
            fulfillmentStatus: 'LINKED',
            status: 'ACTIVE'
        }).subscribe({
            error: error => this.messageService.add({
                severity: 'warn',
                summary: 'Requirement link',
                detail: error?.error?.message || 'Receipt was saved, but requirement fulfillment was not linked.'
            })
        });
    }

    cancel(): void {
        this.router.navigate(['/accounts/enterprise/receivables/generated-receipts']);
    }

    showNewBankAccountDialog(): void {
        const party = this.receiptEntryForm.get('party')?.value as Party | null;
        if (!party) {
            this.messageService.add({ severity: 'warn', summary: 'Party Not Selected', detail: 'Please select a party to add a bank account.' });
            return;
        }

        this.dialogRef = this.dialogService.open(BankAccountEntryComponent, {
            header: 'Create New Bank Account',
            width: '60%',
            contentStyle: { "max-height": "500px", "overflow": "auto" },
            baseZIndex: 10000,
            modal: true,
            data: {
                ownerType: party.id.entityType,
                ownerId: party.id.entityId,
                ownerName: party.entityName,
                isDialog: true
            }
        });

        this.dialogRef.onClose.subscribe((newAccount: BankAccount) => {
            if (newAccount) {
                this.partyBankAccounts = [...this.partyBankAccounts, newAccount];
                this.receiptEntryForm.get('partyBankAccount')?.setValue(newAccount);
                this.messageService.add({ severity: 'success', summary: 'Bank Account Added', detail: 'New bank account has been created and selected.' });
            }
        });
    }

    private partyFromCrmClient(client: CrmClient | null | undefined): Party | null {
        const owningCompanyId = this.sessionStorageService.getCompany()?.id || 0;
        if (client?.linkedCompanyId) {
            return {
                id: { entityId: client.linkedCompanyId, entityType: PartyType.Company },
                entityName: client.companyName,
                emailAddress: client.primaryEmail,
                phoneNumber: client.phoneNumber || client.mobileNumber,
                isCompany: true,
                crNumber: client.crNumber ? String(client.crNumber) : undefined,
                taxRegNumber: client.taxRegNumber,
                owningCompanyId
            };
        }
        if (client?.linkedCustomerId) {
            return {
                id: { entityId: client.linkedCustomerId, entityType: PartyType.Customer },
                entityName: client.companyName,
                emailAddress: client.primaryEmail,
                phoneNumber: client.phoneNumber || client.mobileNumber,
                isCompany: false,
                crNumber: client.crNumber ? String(client.crNumber) : undefined,
                taxRegNumber: client.taxRegNumber,
                owningCompanyId
            };
        }
        return null;
    }

    private partyMatchesClient(party: Party | undefined, client: CrmClient): boolean {
        if (!party?.id) {
            return false;
        }
        return (party.id.entityType === PartyType.Company && party.id.entityId === client.linkedCompanyId)
            || (party.id.entityType === PartyType.Customer && party.id.entityId === client.linkedCustomerId);
    }

    private defaultCurrencyCode(): string {
        return this.sessionStorageService.getCountryList()
            ?.find(value => value.id == this.sessionStorageService.getCompany()?.countryId)
            ?.currencyCode || 'SAR';
    }

    private hasCurrencyMismatch(): boolean {
        return this.outstandingReferences
            .filter(ref => ref.selected && ref.allocatedAmount > 0)
            .some(ref => (ref.currencyCode || this.selectedCurrencyCode) !== this.selectedCurrencyCode);
    }

    private paymentSourceReference(): string {
        const mode = this.receiptEntryForm.get('modeOfPayment')?.value || 'Receipt';
        const transaction = this.receiptEntryForm.get('paymentTransactionNumber')?.value;
        return transaction ? `${mode}: ${transaction}` : mode;
    }
}
